import type {
  CreateReportBody,
  EndStoppageBody,
  ListReportsQuery,
  ObraLedgerView,
  Paginated,
  ReportSummary,
  StartStoppageBody,
  StoppageEntry,
  VideoStatus,
} from '@obras/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api, queryString } from '../../lib/api.js';
import { documentosKey } from '../documentos/api.js';
import { obraKeys } from '../obras/api.js';

export const reportKeys = {
  all: ['relatorios'] as const,
  list: (filters: Partial<ListReportsQuery>) => ['relatorios', 'list', filters] as const,
  ledger: (obraId: string) => ['relatorios', 'razao', obraId] as const,
  stoppages: (obraId: string) => ['relatorios', 'paralisacoes', obraId] as const,
};

export function useReports(filters: Partial<ListReportsQuery>, enabled = true) {
  return useQuery({
    queryKey: reportKeys.list(filters),
    queryFn: ({ signal }) =>
      api.get<Paginated<ReportSummary>>(
        `/relatorios${queryString({
          page: filters.page ?? 1,
          perPage: filters.perPage ?? 20,
          obraId: filters.obraId,
          type: filters.type,
          authorId: filters.authorId,
          status: filters.status,
          // Só vai à query quando ligado: ausente é o padrão do servidor.
          includeDeleted: filters.includeDeleted ? 'true' : undefined,
        })}`,
        signal,
      ),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useObraLedger(obraId: string) {
  return useQuery({
    queryKey: reportKeys.ledger(obraId),
    queryFn: ({ signal }) => api.get<ObraLedgerView>(`/obras/${obraId}/razao`, signal),
  });
}

export function useStoppages(obraId: string) {
  return useQuery({
    queryKey: reportKeys.stoppages(obraId),
    queryFn: ({ signal }) => api.get<StoppageEntry[]>(`/obras/${obraId}/paralisacoes`, signal),
  });
}

/**
 * Publicar um relatório muda o saldo da obra e o prazo de visita.
 * Invalidar obra e razão junto evita que a tela mostre número velho.
 *
 * O dossiê entra na lista porque os arquivos do relatório aparecem lá: sem
 * isso, o laudo anexado agora só chegaria à aba de arquivos na próxima visita
 * à página.
 */
function useObraRefresh(obraId: string) {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: reportKeys.all });
    void queryClient.invalidateQueries({ queryKey: obraKeys.detail(obraId) });
    void queryClient.invalidateQueries({ queryKey: obraKeys.all });
    void queryClient.invalidateQueries({ queryKey: documentosKey(obraId) });
  };
}

export function useCreateReport(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (body: CreateReportBody) =>
      api.post<ReportSummary>(`/obras/${obraId}/relatorios`, body),
    onSuccess: refresh,
  });
}

export function useAttachMedia(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (input: { reportId: string; mediaIds: string[] }) =>
      api.post<ReportSummary>(`/relatorios/${input.reportId}/anexos`, {
        mediaIds: input.mediaIds,
      }),
    onSuccess: refresh,
  });
}

/**
 * Remover relatório.
 *
 * `POST .../remover` e não `DELETE`, porque a justificativa vai no corpo — e
 * corpo em `DELETE` é território onde proxy e cliente HTTP discordam.
 */
export function useRemoveReport(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (input: { reportId: string; reason: string }) =>
      api.post<void>(`/relatorios/${input.reportId}/remover`, { reason: input.reason }),
    onSuccess: refresh,
  });
}

export function useRestoreReport(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (reportId: string) => api.post<void>(`/relatorios/${reportId}/restaurar`, {}),
    onSuccess: refresh,
  });
}

export function useReverseMovement(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (input: { movementId: string; reason: string }) =>
      api.post<ObraLedgerView>(`/movimentacoes/${input.movementId}/estorno`, {
        reason: input.reason,
      }),
    onSuccess: refresh,
  });
}

export function useStartStoppage(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (body: StartStoppageBody) =>
      api.post<StoppageEntry[]>(`/obras/${obraId}/paralisacoes`, body),
    onSuccess: refresh,
  });
}

export function useEndStoppage(obraId: string) {
  const refresh = useObraRefresh(obraId);

  return useMutation({
    mutationFn: (body: EndStoppageBody) =>
      api.post<StoppageEntry[]>(`/obras/${obraId}/paralisacoes/encerrar`, body),
    onSuccess: refresh,
  });
}

export interface UploadedMedia {
  readonly id: string;
  readonly mime: string;
  readonly sizeBytes: number;
  readonly deduplicated: boolean;
  /** Só vem no vídeo: fora do Stream, o arquivo já nasce pronto. */
  readonly status?: VideoStatus;
}

export function uploadMedia(file: File, scope = 'relatorios'): Promise<UploadedMedia> {
  return api.upload<UploadedMedia>(`/midia/upload?scope=${scope}`, file);
}

/**
 * Vídeo tem rota própria porque o destino é outro: a biblioteca do Bunny
 * Stream, que transcodifica antes de o arquivo poder ser assistido.
 */
export function uploadVideo(file: File): Promise<UploadedMedia> {
  return api.upload<UploadedMedia>('/midia/video', file);
}

/**
 * Como anda a transcodificação de um vídeo já enviado.
 *
 * O Bunny não avisa quando termina, e a situação gravada na hora do envio é
 * sempre "processando" — então alguém precisa perguntar. Esta consulta é esse
 * alguém: a rota devolve a situação **e** atualiza o registro de passagem, e
 * quando o vídeo fica pronto os relatórios são invalidados para a tela trocar o
 * quadro de espera pela capa.
 */
export function useVideoStatus(mediaId: string, ativo: boolean) {
  const queryClient = useQueryClient();

  const consulta = useQuery({
    queryKey: ['midia', 'video', mediaId],
    queryFn: ({ signal }) =>
      api.get<{ status: VideoStatus; progress: number }>(`/midia/video/${mediaId}/status`, signal),
    enabled: ativo,
    // Enquanto processa, de quatro em quatro segundos; depois, para.
    refetchInterval: (consulta) =>
      consulta.state.data && consulta.state.data.status !== 'processando' ? false : 4000,
  });

  const situacao = consulta.data?.status;

  useEffect(() => {
    if (!situacao || situacao === 'processando') return;
    void queryClient.invalidateQueries({ queryKey: reportKeys.all });
  }, [situacao, queryClient]);

  return consulta;
}
